import React, { useEffect, useRef, useState } from 'react';
import {
  CafAttachment, CafClusterSample, getEmailPreview, listClusterMembers,
} from '../../api/cafCorpus';
import { FinalResponseModal } from './FinalResponseModal';
import { EmailBodyView } from './EmailBody';
import { fileKind, formatCount, formatSize, formatWhen } from './format';
import { Icon } from './Icon';
import { useToast } from './ToastContext';

export const Attachments: React.FC<{ items: CafAttachment[] }> = ({ items }) => {
  if (!items.length) return null;
  return (
    <div className="cur-att-list">
      {items.map((item) => (
        <div key={item.id} className={`cur-att${item.download_url ? '' : ' cur-att--missing'}`}>
          <Icon name="clip" size="sm" />
          {item.download_url
            ? <a className="cur-att-name" href={item.download_url} target="_blank" rel="noopener noreferrer" title={item.name}>{item.name}</a>
            : <span className="cur-att-name" title={item.name}>{item.name}</span>}
          <span className="cur-att-meta">
            {item.download_url
              ? [fileKind(item.contentType, item.name), formatSize(item.size)].filter(Boolean).join(' · ')
              : 'File not available'}
          </span>
        </div>
      ))}
    </div>
  );
};

interface SelectionProps {
  selectedIds?: string[];
  onSelect?: (id: string) => void;
  selectionDisabled?: boolean;
}

export const EmailSamples: React.FC<{ samples: CafClusterSample[]; allowMarkFinal: boolean } & SelectionProps> = ({ samples, allowMarkFinal, selectedIds, onSelect, selectionDisabled }) => {
  const toast = useToast();
  const [correcting, setCorrecting] = useState<CafClusterSample | null>(null);
  const [shown, setShown] = useState<Record<string, boolean>>({});
  // A full-body read belongs to the exact parent payload that started it.
  // New props invalidate that read instead of being masked by its old flag.
  const [full, setFull] = useState<Record<string, { sample: CafClusterSample; baseline: string }>>({});
  const incoming = useRef(new Map<string, string>());
  incoming.current = new Map(samples.map((sample) => [sample.id, JSON.stringify(sample)]));
  const previousInputs = useRef(new Map<string, string>());
  const readVersions = useRef<Record<string, number>>({});
  const mounted = useRef(true);
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [problems, setProblems] = useState<Record<string, string>>({});
  useEffect(() => {
    const changed = Array.from(incoming.current.keys()).filter((id) =>
      previousInputs.current.has(id) && previousInputs.current.get(id) !== incoming.current.get(id));
    previousInputs.current = new Map(incoming.current);
    if (!changed.length) return;
    changed.forEach((id) => { readVersions.current[id] = (readVersions.current[id] ?? 0) + 1; });
    setFull((prev) => { const next = { ...prev }; changed.forEach((id) => { delete next[id]; }); return next; });
    setLoading((prev) => { const next = { ...prev }; changed.forEach((id) => { next[id] = false; }); return next; });
    setProblems((prev) => { const next = { ...prev }; changed.forEach((id) => { delete next[id]; }); return next; });
  }, [samples]);

  useEffect(() => {
    mounted.current = true;
    const receive = (event: Event) => {
      const fresh = (event as CustomEvent<CafClusterSample>).detail;
      const baseline = incoming.current.get(fresh.id);
      if (baseline === undefined) return;
      readVersions.current[fresh.id] = (readVersions.current[fresh.id] ?? 0) + 1;
      setFull((prev) => ({ ...prev, [fresh.id]: { sample: fresh, baseline } }));
      setLoading((prev) => ({ ...prev, [fresh.id]: false }));
      setProblems((prev) => ({ ...prev, [fresh.id]: '' }));
    };
    window.addEventListener('caf-final-response-refreshed', receive);
    return () => { mounted.current = false; window.removeEventListener('caf-final-response-refreshed', receive); };
  }, []);

  const read = async (id: string) => {
    const baseline = incoming.current.get(id);
    if (baseline === undefined || (full[id] && full[id].baseline === baseline) || loading[id]) return;
    const version = (readVersions.current[id] ?? 0) + 1;
    readVersions.current[id] = version;
    const current = () => mounted.current && readVersions.current[id] === version && incoming.current.get(id) === baseline;
    setLoading((prev) => ({ ...prev, [id]: true }));
    try {
      const preview = await getEmailPreview(id);
      if (!current()) return;
      setFull((prev) => ({ ...prev, [id]: { sample: preview, baseline } }));
      setProblems((prev) => ({ ...prev, [id]: '' }));
    } catch {
      if (!current()) return;
      setProblems((prev) => ({ ...prev, [id]: "Couldn't load the whole email. The start is shown." }));
    } finally {
      if (current()) setLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  const toggle = (id: string) => {
    const opening = !shown[id];
    setShown((prev) => ({ ...prev, [id]: opening }));
    if (opening) void read(id);
  };

  return (
    <div className="cur-samples">
      {correcting && <FinalResponseModal sample={correcting} onClose={() => setCorrecting(null)} onSaved={(fresh) => {
        window.dispatchEvent(new CustomEvent('caf-final-response-refreshed', { detail: fresh }));
        toast.saved('Final-response status saved.');
      }} />}
      {samples.map((sample) => {
        const cached = full[sample.id];
        const view = cached?.baseline === JSON.stringify(sample) ? cached.sample : sample;
        const isOpen = Boolean(shown[sample.id]);
        const loaded = cached?.baseline === JSON.stringify(sample);
        const known = view.final_response_value_known !== false && typeof view.has_final_response === 'boolean';
        return (
          <article key={sample.id} className="cur-sample" aria-label={sample.subject || 'Email without a subject'}>
            <div className="cur-sample-head">
              {onSelect && <input type="checkbox" aria-label={`Select email: ${sample.subject || sample.id}`}
                checked={Boolean(selectedIds?.includes(sample.id))} disabled={selectionDisabled}
                onChange={() => onSelect(sample.id)} />}
              <div className="cur-sample-subj">{sample.subject || '(No subject)'}</div>
              <div className="cur-sample-date">{formatWhen(sample.received_date)}</div>
            </div>
            <Attachments items={view.attachments ?? []} />
            {view.attachments_unavailable && <p className="cur-hint">Attachment details are unavailable; this does not mean there are no attachments.</p>}
            <div className="cur-sample-actions">
              <button type="button" className="ui-btn ui-btn--ghost ui-btn--sm" aria-expanded={isOpen}
                onClick={() => { void toggle(sample.id); }}>{isOpen ? 'Hide email' : 'Read email'}</button>
              <span className="cur-hint">Final response: {known ? (view.has_final_response ? 'Yes' : 'No') : 'Unknown'} · {view.final_response_provenance === 'staff' ? 'Staff assessment' : view.final_response_provenance === 'ai' ? 'AI assessment' : 'Origin unknown'}{view.final_response_needs_review ? ' · Needs review' : ''}</span>
            </div>
            {isOpen && (loading[sample.id]
              ? <p className="cur-hint">Loading the email…</p>
              : (
                <>
                  {problems[sample.id] && <p className="cur-hint" role="alert">{problems[sample.id]}</p>}
                  {!loaded && <button type="button" className="cur-linkish" onClick={() => { void read(sample.id); }}>Retry reading email</button>}
                  <EmailBodyView body={view.body ?? ''} title={`Email: ${sample.subject || 'no subject'}`} />
                  {view.truncated && <p className="cur-hint">This email is long. The start is shown.</p>}
                  {view.final_response_provenance === 'staff' && <p className="cur-hint">
                    Staff review{view.final_response_actor ? ` by ${view.final_response_actor}` : ''}{view.final_response_changed_at ? ` · ${formatWhen(view.final_response_changed_at)}` : ''}.
                  </p>}
                  {loaded && <div className="cur-reader-review">
                    <p className="cur-hint">Attachments may contain the final response. Review their contents where available; opening this email does not confirm they have been reviewed.</p>
                    {allowMarkFinal && <button type="button" className="cur-linkish" onClick={() => setCorrecting(view)}>Review final-response status</button>}
                  </div>}
                </>
              ))}
          </article>
        );
      })}
    </div>
  );
};

type AllEmailsProps = { clusterId: string; total: number; allowMarkFinal: boolean } & SelectionProps;
// Changing groups creates a new paging owner, including its refresh journal.
export const AllEmails: React.FC<AllEmailsProps> = (props) => <ClusterEmails key={props.clusterId} {...props} />;
const ClusterEmails: React.FC<AllEmailsProps> = ({ clusterId, total, allowMarkFinal, ...selection }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<CafClusterSample[]>([]);
  const [next, setNext] = useState<number | null>(0);
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState('');
  const refreshVersion = useRef(0);
  const mounted = useRef(true);
  const refreshed = useRef(new Map<string, { version: number; sample: CafClusterSample }>());
  // This owner remains mounted while its member cards are hidden. Persist
  // refreshed rows here and merge saves that race an older page request.
  useEffect(() => {
    mounted.current = true;
    const receive = (event: Event) => {
      const fresh = (event as CustomEvent<CafClusterSample>).detail;
      refreshVersion.current += 1;
      refreshed.current.set(fresh.id, { version: refreshVersion.current, sample: fresh });
      setItems((prev) => prev.map((item) => item.id === fresh.id ? fresh : item));
    };
    window.addEventListener('caf-final-response-refreshed', receive);
    return () => { mounted.current = false; window.removeEventListener('caf-final-response-refreshed', receive); };
  }, []);
  const load = async (offset: number) => {
    if (loading) return;
    const started = refreshVersion.current;
    setLoading(true);
    setProblem('');
    try {
      const page = await listClusterMembers(clusterId, { offset, limit: 25 });
      if (!mounted.current) return;
      const freshItems = page.items.map((item) => {
        const saved = refreshed.current.get(item.id);
        return saved && saved.version > started ? saved.sample : item;
      });
      setItems((prev) => (offset === 0 ? freshItems : [...prev, ...freshItems]));
      setNext(page.next_offset);
    } catch {
      if (!mounted.current) return;
      setProblem("Couldn't load more emails. Try again.");
    } finally {
      if (mounted.current) setLoading(false);
    }
  };
  return (
    <div>
      <button type="button" className="cur-linkish" aria-expanded={open}
        onClick={() => { const opening = !open; setOpen(opening); if (opening && !items.length) void load(0); }}>
        {open ? 'Hide the other emails' : `Show all ${formatCount(total)} emails`}
      </button>
      {open && (
        <div style={{ marginTop: 10 }}>
          {problem && <p className="cur-hint" role="alert">{problem}</p>}
          <EmailSamples samples={items} allowMarkFinal={allowMarkFinal} {...selection} />
          {loading && <p className="cur-hint">Loading…</p>}
          {next !== null && !loading && (
            <div className="cur-more-row">
              <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => { void load(next); }}>Show more</button>
            </div>
          )}
          <p className="cur-hint">Showing {formatCount(items.length)} of {formatCount(total)}.</p>
        </div>
      )}
    </div>
  );
};
